(() => {
  if (customElements.get('mimsy-ugc')) return;
  const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
  customElements.define('mimsy-ugc', class extends HTMLElement {
    connectedCallback() {
      this.events?.abort();
      this.events = new AbortController();
      this.dialog = this.querySelector('dialog');
      this.player = this.querySelector('[data-ugc-player]');
      this.track = this.querySelector('[data-ugc-track]');
      const on = (node, event, fn, options = {}) => node?.addEventListener(event, fn, { ...options, signal: this.events.signal });
      on(this, 'click', event => {
        const trigger = event.target.closest('[data-ugc-play]');
        if (trigger && this.contains(trigger)) this.openVideo(trigger);
        const step = event.target.closest('[data-ugc-step]');
        if (step) this.move(Number(step.dataset.ugcStep));
      });
      on(this.querySelector('[data-ugc-close]'), 'click', () => this.dialog.close());
      on(this.dialog, 'click', event => {
        if (event.target !== this.dialog) return;
        const bounds = this.dialog.getBoundingClientRect();
        if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) this.dialog.close();
      });
      on(this.dialog, 'close', () => this.clearPlayer(true));
      on(document, 'visibilitychange', () => { if (document.hidden) this.player?.querySelector('video')?.pause(); });
      on(document, 'play', event => {
        if (event.target.tagName !== 'VIDEO') return;
        document.querySelectorAll('video').forEach(video => { if (video !== event.target) video.pause(); });
      }, { capture: true });
      if (this.track) {
        this.originals = Array.from(this.track.children).filter(card => card.matches('.mimsy-ugc__card') && !card.hasAttribute('data-ugc-clone'));
        on(this.track, 'scroll', () => this.handleScroll(), { passive: true });
        on(this.track, 'scrollend', () => this.recenter());
        on(this.track, 'pointerdown', () => { this.dragging = true; clearTimeout(this.settleTimer); }, { passive: true });
        on(window, 'pointerup', () => { this.dragging = false; this.scheduleRecenter(); }, { passive: true });
        on(window, 'pointercancel', () => { this.dragging = false; this.scheduleRecenter(); }, { passive: true });
        on(this.track, 'keydown', event => {
          if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
          event.preventDefault();
          const rtl = getComputedStyle(this.track).direction === 'rtl';
          this.move((event.key === 'ArrowRight' ? 1 : -1) * (rtl ? -1 : 1));
        });
        on(document, 'shopify:block:select', event => {
          const card = event.target.closest('.mimsy-ugc__card');
          if (card && this.track.contains(card)) card.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'instant' });
        });
        this.resize?.disconnect();
        this.resize = new ResizeObserver(() => this.layoutLoop());
        this.resize.observe(this.track);
        this.layoutLoop();
      }
      this.setAttribute('data-ready', '');
    }
    openVideo(trigger) {
      const clone = trigger.closest('[data-ugc-clone]');
      if (clone) {
        trigger = this.originals[Number(clone.dataset.ugcClone)]?.querySelector('[data-ugc-play]');
        if (!trigger) return;
        trigger.focus({ preventScroll: true });
      }
      const template = trigger.parentElement.querySelector('[data-ugc-template]');
      if (!template || !this.dialog || !this.player) return;
      document.querySelectorAll('mimsy-ugc dialog[open]').forEach(dialog => { if (dialog !== this.dialog) dialog.close(); });
      this.clearPlayer(false);
      this.returnTarget = trigger;
      this.player.append(template.content.cloneNode(true));
      const video = this.player.querySelector('video');
      if (!video) return;
      this.querySelector('[data-ugc-title]').textContent = trigger.dataset.videoLabel;
      video.setAttribute('aria-label', trigger.dataset.videoLabel);
      video.controls = true;
      video.playsInline = true;
      video.muted = false;
      this.dialog.showModal();
      if ('IntersectionObserver' in window) {
        this.visibility = new IntersectionObserver(([entry]) => { if (!entry.isIntersecting) video.pause(); }, { threshold: .1 });
        this.visibility.observe(video);
      }
      video.play().catch(() => {});
    }
    clearPlayer(restoreFocus) {
      this.visibility?.disconnect();
      this.player?.querySelectorAll('video').forEach(video => {
        video.pause();
        video.removeAttribute('src');
        video.querySelectorAll('source').forEach(source => source.removeAttribute('src'));
        video.load();
      });
      this.player?.replaceChildren();
      if (restoreFocus && this.returnTarget?.isConnected) {
        this.returnTarget.focus({ preventScroll: true });
        if (this.track?.contains(this.returnTarget)) {
          const bounds = this.track.getBoundingClientRect();
          const card = this.returnTarget.parentElement.getBoundingClientRect();
          if (card.left < bounds.left || card.right > bounds.right) this.returnTarget.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'instant' });
        }
      }
    }
    scrollPosition() {
      return this.track.scrollLeft * (this.rtl ? -1 : 1);
    }
    jumpTo(position) {
      cancelAnimationFrame(this.snapFrame);
      this.track.classList.add('mimsy-ugc__track--repositioning');
      this.track.scrollTo({ left: position * (this.rtl ? -1 : 1), behavior: 'instant' });
      this.snapFrame = requestAnimationFrame(() => this.track?.classList.remove('mimsy-ugc__track--repositioning'));
    }
    layoutLoop() {
      if (!this.track || !this.originals.length) return;
      const style = getComputedStyle(this.track);
      const gap = parseFloat(style.columnGap || style.gap) || 0;
      const width = this.originals.reduce((total, card) => total + card.getBoundingClientRect().width, 0);
      const signature = `${this.track.clientWidth}:${width}:${gap}:${style.direction}`;
      if (signature === this.layoutSignature) return;
      const phase = this.infinite && this.cycleWidth ? ((this.scrollPosition() % this.cycleWidth) + this.cycleWidth) % this.cycleWidth / this.cycleWidth : 0;
      this.layoutSignature = signature;
      clearTimeout(this.settleTimer);
      this.track.querySelectorAll('[data-ugc-clone]').forEach(card => card.remove());
      this.rtl = style.direction === 'rtl';
      this.cycleWidth = width + gap * this.originals.length;
      this.infinite = this.originals.length > 1 && width + gap * (this.originals.length - 1) > this.track.clientWidth + 2;
      this.track.toggleAttribute('data-ugc-infinite', this.infinite);
      if (this.infinite) {
        const copy = () => {
          const fragment = document.createDocumentFragment();
          this.originals.forEach((card, index) => {
            const clone = card.cloneNode(true);
            clone.dataset.ugcClone = String(index);
            clone.setAttribute('aria-hidden', 'true');
            clone.removeAttribute('data-shopify-editor-block');
            clone.removeAttribute('id');
            clone.querySelectorAll('[id]').forEach(node => node.removeAttribute('id'));
            clone.querySelectorAll('button,a,input,select,textarea,[tabindex]').forEach(node => node.setAttribute('tabindex', '-1'));
            clone.querySelectorAll('noscript').forEach(node => node.remove());
            fragment.append(clone);
          });
          return fragment;
        };
        this.track.prepend(copy());
        this.track.append(copy());
        this.jumpTo(this.cycleWidth * (1 + phase));
      } else {
        this.jumpTo(0);
      }
      this.updateArrows();
    }
    recenter() {
      if (!this.infinite || this.dragging || !this.cycleWidth) return;
      const position = this.scrollPosition();
      // Equivalent positions in adjacent copies render exactly the same cards.
      const target = this.cycleWidth + ((position % this.cycleWidth) + this.cycleWidth) % this.cycleWidth;
      if (Math.abs(position - target) > .5) this.jumpTo(target);
    }
    scheduleRecenter() {
      clearTimeout(this.settleTimer);
      if (this.infinite && !this.dragging) this.settleTimer = setTimeout(() => this.recenter(), 140);
    }
    handleScroll() {
      if (this.infinite) {
        const position = this.scrollPosition();
        const limit = this.track.scrollWidth - this.track.clientWidth;
        const buffer = Math.min(this.cycleWidth / 4, this.track.clientWidth / 2);
        // Rebase before momentum can reach either physical end of the track.
        if (position < buffer || position > limit - buffer) {
          const wasDragging = this.dragging;
          this.dragging = false;
          this.recenter();
          this.dragging = wasDragging;
        }
        this.scheduleRecenter();
      } else this.updateArrows();
    }
    move(step) {
      if (!this.track) return;
      const card = this.originals?.[0];
      if (!card) return;
      this.recenter();
      const gap = parseFloat(getComputedStyle(this.track).gap) || 0;
      const rtl = getComputedStyle(this.track).direction === 'rtl';
      this.track.scrollBy({ left: (card.getBoundingClientRect().width + gap) * step * (rtl ? -1 : 1), behavior: motion.matches ? 'instant' : 'smooth' });
    }
    updateArrows() {
      const cards = this.track?.querySelectorAll('.mimsy-ugc__card');
      if (!cards?.length) return;
      const bounds = this.track.getBoundingClientRect();
      const first = cards[0].getBoundingClientRect();
      const last = cards[cards.length - 1].getBoundingClientRect();
      const rtl = getComputedStyle(this.track).direction === 'rtl';
      const previous = this.querySelector('[data-ugc-step="-1"]');
      const next = this.querySelector('[data-ugc-step="1"]');
      if (this.infinite) {
        if (previous) previous.disabled = false;
        if (next) next.disabled = false;
        return;
      }
      if (previous) previous.disabled = rtl ? first.right <= bounds.right + 3 : first.left >= bounds.left - 3;
      if (next) next.disabled = rtl ? last.left >= bounds.left - 3 : last.right <= bounds.right + 3;
    }
    disconnectedCallback() {
      if (this.dialog?.open) this.dialog.close();
      this.clearPlayer(false);
      this.events?.abort();
      this.resize?.disconnect();
      clearTimeout(this.settleTimer);
      cancelAnimationFrame(this.snapFrame);
      this.track?.querySelectorAll('[data-ugc-clone]').forEach(card => card.remove());
      this.track?.removeAttribute('data-ugc-infinite');
      this.track?.classList.remove('mimsy-ugc__track--repositioning');
      this.layoutSignature = null;
      this.infinite = false;
    }
  });
})();
